import { Fragment, useState, type ReactNode } from 'react'
import { Building2, Calendar, CreditCard } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'

import { useIsPlatformOwner } from '../../../app/session'
import {
  Avatar,
  Banner,
  Button,
  EmptyState,
  KeyValue,
  LoadingSkeleton,
  Meter,
  Money,
  PageHeader,
  QueryBoundary,
  Section,
  SparkBar,
  StatTile,
  StatusBadge,
} from '../../../components'
import { useBusinesses, useOverview, usePlans, type PlanCode } from '../../../fixtures'
import { formatGb, formatLongDate, formatPercent, previousMonthName } from '../../../lib/format'
import { t } from '../../../lib/i18n'
import { routes } from '../../../lib/routes'
import { AddBusinessButton, AddBusinessDialog } from '../AddBusinessDialog'

/**
 * P-01 · Platform overview.
 *
 * Revenue, trials and storage — the three figures that decide whether the
 * business works. Not one number on this screen comes from inside a trader's
 * book: "animals tracked" is a COUNT, and cattle under management is a cost
 * basis the API has already aggregated, never a per-book figure re-derived here.
 *
 * "Add a business" is the SAME dialog P-02 opens (`AddBusinessDialog`), not a
 * second copy of the create form. Until today this button was a toast that
 * described what would happen and did nothing.
 */

/** A count, not money — grouped the Indian way, like every other figure here. */
const COUNT = new Intl.NumberFormat('en-IN')

/**
 * Substitutes React nodes into a `t()` template.
 *
 * A rupee figure may only be rendered by `<Money>`, so it cannot be passed to
 * `t()` as a variable. The template keeps its `{amount}` placeholder and the
 * component is dropped into the hole instead.
 */
function fill(template: string, nodes: Record<string, ReactNode>): ReactNode {
  return template.split(/(\{\w+\})/g).map((part, index) => {
    const name = /^\{(\w+)\}$/.exec(part)?.[1]
    return (
      <Fragment key={index}>{name !== undefined && name in nodes ? nodes[name] : part}</Fragment>
    )
  })
}

export function OverviewScreen() {
  const overview = useOverview()
  const businesses = useBusinesses()
  const plans = usePlans()
  const isOwner = useIsPlatformOwner()
  const navigate = useNavigate()

  const [adding, setAdding] = useState(false)

  /** A plan name is the platform owner's own wording (P-05), so it is data. */
  const planName = (code: PlanCode): string =>
    plans.data?.find((plan) => plan.code === code)?.name ?? code

  return (
    <>
      <PageHeader
        title={t('p.overview.title')}
        subtitle={overview.data ? formatLongDate(overview.data.today) : undefined}
        actions={
          <>
            <span className="gl inline-flex h-10 items-center rounded-[11px] px-4">
              <span className="z flex items-center gap-2 text-[13.5px] font-bold text-dark">
                <Calendar className="size-4 text-muted" />
                {t('p.overview.thisMonth')}
              </span>
            </span>
            {/* Absent for a support account rather than disabled — the gate
                lives inside the button. */}
            <AddBusinessButton onClick={() => setAdding(true)} />
          </>
        }
      />

      <QueryBoundary
        query={overview}
        skeleton={
          <>
            <LoadingSkeleton variant="tiles" />
            <LoadingSkeleton variant="panel" className="mt-3.5" />
          </>
        }
        isEmpty={(data) => data.totalBusinesses === 0}
        empty={
          <EmptyState
            icon={<Building2 className="size-6" />}
            title={t('p.overview.empty')}
            body={t('p.overview.emptyBody')}
            action={isOwner ? <AddBusinessButton onClick={() => setAdding(true)} /> : undefined}
          />
        }
      >
        {(data) => (
          <>
            <Banner
              tone="warn"
              icon={<CreditCard className="size-5" />}
              title={t('p.overview.pastDueTitle', { count: data.pastDueCount })}
              body={fill(
                t('p.overview.pastDueBody', { share: formatPercent(data.pastDueShare) }),
                { amount: <Money paisa={data.pastDueAmountPaisa} /> },
              )}
              action={
                <Button onClick={() => navigate(routes.platform.subscriptions())}>
                  {t('p.overview.review')}
                </Button>
              }
            />

            <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
              <StatTile
                surface="heavy"
                label={t('p.overview.monthlyRevenue')}
                value={<Money paisa={data.monthlyRevenuePaisa} short={false} />}
                sub={fill(t('p.overview.revenueDelta', { month: previousMonthName(data.today) }), {
                  amount: (
                    <Money
                      paisa={data.revenueDeltaPaisa}
                      signed
                      tone="positive"
                      className="text-[11.5px]"
                    />
                  ),
                })}
              />
              <StatTile
                surface="heavy"
                label={t('p.overview.paying')}
                value={COUNT.format(data.payingCount)}
                sub={fill(t('p.overview.payingSub'), {
                  amount: <Money paisa={data.averagePerBusinessPaisa} className="text-[11.5px]" />,
                })}
              />
              <StatTile
                surface="heavy"
                label={t('p.overview.onTrial')}
                value={<span className="text-warn">{COUNT.format(data.trialCount)}</span>}
                sub={t('p.overview.trialSub')}
              />
              <StatTile
                surface="heavy"
                label={t('p.overview.animals')}
                value={COUNT.format(data.animalsTracked)}
                sub={t('p.overview.animalsSub')}
              />
            </div>

            <div className="mt-3.5 grid gap-3.5 sm:grid-cols-2 xl:grid-cols-3">
              <StatTile
                surface="accent"
                label={t('p.overview.underManagement')}
                value={<Money paisa={data.cattleUnderManagementPaisa} short />}
                sub={t('p.overview.underManagementSub', { count: data.totalBusinesses })}
              />
              <StatTile
                surface="accent"
                label={t('p.overview.profitBooked')}
                value={<Money paisa={data.profitBookedPaisa} short signed tone="positive" />}
                sub={t('p.overview.profitBookedSub')}
              />
              <StatTile
                surface="accent"
                label={t('p.overview.yourCut')}
                value={formatPercent(data.cutOfProfit, 2)}
                sub={fill(t('p.overview.yourCutSub'), {
                  revenue: <Money paisa={data.monthlyRevenuePaisa} className="text-[11.5px]" />,
                  profit: (
                    <Money paisa={data.profitBookedPaisa} short className="text-[11.5px]" />
                  ),
                })}
              />
            </div>

            <div className="mt-3.5 grid gap-3.5 lg:grid-cols-[1.55fr_1fr]">
              <Section
                title={t('p.overview.payingChart')}
                action={
                  <span className="text-xs text-muted">{t('p.overview.lastSixMonths')}</span>
                }
              >
                <SparkBar
                  ariaLabel={t('p.overview.payingChart')}
                  data={data.payingByMonth.map((point) => ({
                    label: point.month,
                    value: point.count,
                  }))}
                />
                <div className="mt-3.5 border-t border-muted/15 pt-1">
                  <KeyValue
                    label={t('p.overview.newThisMonth')}
                    value={COUNT.format(data.newThisMonth)}
                  />
                  <KeyValue label={t('p.overview.trialsConverted')} value={data.trialsConverted} />
                  <KeyValue
                    label={t('p.overview.cancelled')}
                    value={COUNT.format(data.cancelled)}
                  />
                </div>
              </Section>

              <Section
                title={t('p.overview.newest')}
                action={
                  <Link
                    to={routes.platform.businesses()}
                    className="font-display text-xs font-bold text-accent-dark"
                  >
                    {t('act.viewAll')}
                  </Link>
                }
              >
                <QueryBoundary
                  query={businesses}
                  skeleton={<LoadingSkeleton variant="list" rows={5} />}
                  isEmpty={(rows) => rows.length === 0}
                  empty={
                    <EmptyState
                      icon={<Building2 className="size-6" />}
                      title={t('p.overview.empty')}
                      body={t('p.overview.emptyBody')}
                    />
                  }
                >
                  {(rows) => (
                    <ul>
                      {rows.slice(0, 5).map((business) => (
                        <li key={business.id}>
                          <Link
                            to={routes.platform.business(business.slug)}
                            className="flex items-center gap-3 border-b border-muted/12 py-2.5 last:border-b-0"
                          >
                            <Avatar name={business.name} />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-[13.5px] font-semibold text-dark">
                                {business.name}
                              </span>
                              <span className="block truncate text-[11.5px] text-muted">
                                {business.city} · {COUNT.format(business.animals)}{' '}
                                {t('unit.animals')}
                              </span>
                            </span>
                            <StatusBadge tone={business.plan === 'TRIAL' ? 'warn' : 'ok'}>
                              {planName(business.plan)}
                            </StatusBadge>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </QueryBoundary>
              </Section>
            </div>

            <div className="mt-3.5 grid gap-3.5 sm:grid-cols-2 xl:grid-cols-3">
              <Section title={t('p.overview.storage')}>
                <p className="font-display text-[22px] font-extrabold tracking-[-0.6px] tnum text-dark">
                  {t('p.usage.ofLimit', {
                    used: formatGb(data.storageUsedGb),
                    limit: formatGb(data.storageLimitGb),
                  })}
                </p>
                <Meter
                  value={data.storageUsedGb}
                  max={data.storageLimitGb}
                  label={t('p.overview.storage')}
                />
                <p className="mt-2.5 text-[12.5px] leading-relaxed text-muted">
                  {t('p.overview.storageBody')}
                </p>
              </Section>

              <Section title={t('p.overview.backups')}>
                <p className="font-display text-[22px] font-extrabold tracking-[-0.6px] tnum text-success">
                  {t('p.usage.ofLimit', {
                    used: COUNT.format(data.backupsDone),
                    limit: COUNT.format(data.backupsTotal),
                  })}
                </p>
                <Meter
                  value={data.backupsDone}
                  max={data.backupsTotal}
                  tone="accent"
                  label={t('p.overview.backups')}
                />
                <p className="mt-2.5 text-[12.5px] leading-relaxed text-muted">
                  {/* Only the counts the payload gives, and only the sentence
                      they support. */}
                  {data.backupsDone >= data.backupsTotal
                    ? t('p.overview.backupsBody')
                    : t('p.overview.backupsBodyPartial', {
                        count: COUNT.format(data.backupsDone),
                        total: COUNT.format(data.backupsTotal),
                      })}
                </p>
              </Section>

              <Section title={t('p.overview.videoQueue')}>
                <p className="font-display text-[22px] font-extrabold tracking-[-0.6px] tnum text-dark">
                  {COUNT.format(data.videoQueue)}
                  <span className="ms-1.5 font-body text-[13px] font-semibold text-muted">
                    {t('p.overview.videoQueueValue')}
                  </span>
                </p>
                {/* No <Meter> here: a queue depth has no denominator, and the
                    25 that used to be one was not a figure anything reports. */}
                <p className="mt-2.5 text-[12.5px] leading-relaxed text-muted">
                  {data.videoFailed24h === 0
                    ? t('p.overview.videoQueueBodyNone')
                    : t('p.overview.videoQueueBody', {
                        count: COUNT.format(data.videoFailed24h),
                      })}
                </p>
              </Section>
            </div>
          </>
        )}
      </QueryBoundary>

      <AddBusinessDialog open={adding} onClose={() => setAdding(false)} />
    </>
  )
}
